import React, { useEffect, useMemo, useState } from 'react'
import { Head, Deferred, usePage } from '@inertiajs/react'
import DashboardLayout from '@/frontend/layouts/DashboardLayout'
import type { SharedProps } from '@/frontend/types/inertia'
import { EmptyTeamState } from '@/frontend/features/Home/EmptyTeamState'
import { trackHomeViewed } from '@/frontend/features/Home/track'
import { WorkGreeting } from '@/frontend/features/HomeWork/WorkGreeting'
import { ActiveConversations } from '@/frontend/features/HomeWork/ActiveConversations'
import { UpcomingRuns, UpcomingRunsSkeleton } from '@/frontend/features/HomeWork/UpcomingRuns'
import { RecentRuns, RecentRunsSkeleton } from '@/frontend/features/HomeWork/RecentRuns'
import { RecentArtifacts } from '@/frontend/features/HomeWork/RecentArtifacts'
import { ProcessesPanel } from '@/frontend/features/HomeWork/ProcessesPanel'
import { NeedsYou } from '@/frontend/features/Home/NeedsYou'
import type { OrgCron, WorkExecution, WorkProcess, WorkWorker, WorkFeedPayload } from '@/frontend/features/HomeWork/types'

interface Props {
  page_title: string
  workers: WorkWorker[]
  processes: WorkProcess[]
  org_crons?: OrgCron[]
  org_executions?: WorkExecution[]
  home_feed?: WorkFeedPayload
}

function ProvisioningCard({ workers }: { workers: WorkWorker[] }) {
  if (workers.length === 0) return null

  return (
    <div className="space-y-1.5">
      {workers.map((worker) => (
        <div
          key={worker.id}
          className="flex items-center gap-2.5 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
        >
          <span className="inline-block h-1.5 w-1.5 animate-live-pulse rounded-full bg-warning" aria-hidden />
          <span className="font-semibold text-foreground">{worker.name}</span>
          <span>está sendo preparado — isso leva alguns minutos.</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Home de gestão de trabalho (Iteração 4): central de trabalho da org, organizada
 * em torno da EXECUÇÃO — a unidade que mescla processos e rotinas (o vínculo é
 * business_processes.cron_job_ids; ambos viram fleet_sessions channel=cron).
 * Ordem: cabeçalho + filtro por agente → Próximas execuções → Últimas execuções
 * → Conversas ativas | Publicações recentes (lado a lado). O filtro recorta a
 * home inteira para um colaborador, no cliente (todas as listas têm worker_id).
 * O chat é acesso secundário (sidebar / ⌘N). Peças de trabalho são deferidas.
 */
function HomeIndex({ page_title, workers, processes, org_crons, org_executions, home_feed }: Props) {
  const { props: shared } = usePage<SharedProps>()
  const [filterWorkerId, setFilterWorkerId] = useState<string>('all')

  useEffect(() => {
    trackHomeViewed()
  }, [])

  const provisioningWorkers = useMemo(
    () => workers.filter((w) => w.provisioning_status !== 'ready' && w.provisioning_status !== 'failed'),
    [workers],
  )
  const workerAvatars = useMemo(
    () => Object.fromEntries(workers.map((worker) => [worker.id, worker.avatar_url])),
    [workers],
  )

  // Recorte por agente no cliente — todas as listas carregam worker_id, então
  // filtrar é instantâneo e não precisa de round-trip. 'all' = a org inteira.
  const matches = (workerId: string | null | undefined) => filterWorkerId === 'all' || workerId === filterWorkerId

  const filteredProvisioning = useMemo(() => provisioningWorkers.filter((w) => matches(w.id)), [provisioningWorkers, filterWorkerId])
  const filteredCrons = useMemo(() => (org_crons ?? []).filter((c) => matches(c.worker_id)), [org_crons, filterWorkerId])
  const filteredExecutions = useMemo(() => (org_executions ?? []).filter((e) => matches(e.worker_id)), [org_executions, filterWorkerId])
  const filteredProcesses = useMemo(() => processes.filter((p) => matches(p.worker_id)), [processes, filterWorkerId])
  const filteredApprovals = useMemo(() => (home_feed?.approvals ?? []).filter((a) => matches(a.worker_id)), [home_feed, filterWorkerId])
  const filteredConversations = useMemo(() => (home_feed?.conversations ?? []).filter((c) => matches(c.worker_id)), [home_feed, filterWorkerId])
  const filteredArtifacts = useMemo(() => (home_feed?.artifacts ?? []).filter((a) => matches(a.worker_id)), [home_feed, filterWorkerId])
  const approvalQueueEnabled = shared.featureFlags?.approval_queue_enabled ?? false

  return (
    <>
      <Head title={page_title} />
      <div className="mx-auto w-full max-w-[1000px] animate-fade-up p-4 md:p-8">
        <WorkGreeting
          userName={shared.auth?.user?.name ?? null}
          workerCount={workers.length}
          workers={workers}
          filterWorkerId={filterWorkerId}
          onFilterChange={setFilterWorkerId}
        />

        {workers.length === 0 ? (
          <div className="mt-10">
            <EmptyTeamState />
          </div>
        ) : (
          <div className="mt-6 space-y-7">
            <ProvisioningCard workers={filteredProvisioning} />

            {approvalQueueEnabled && (
              <Deferred data="home_feed" fallback={<></>}>
                <NeedsYou items={filteredApprovals} />
              </Deferred>
            )}

            <ProcessesPanel processes={filteredProcesses} workerAvatars={workerAvatars} />

            <Deferred data="org_crons" fallback={<UpcomingRunsSkeleton />}>
              <UpcomingRuns crons={filteredCrons} workerAvatars={workerAvatars} />
            </Deferred>

            <Deferred data="org_executions" fallback={<RecentRunsSkeleton />}>
              <RecentRuns executions={filteredExecutions} workerAvatars={workerAvatars} />
            </Deferred>

            <div className="grid gap-x-5 gap-y-7 md:grid-cols-2">
              <Deferred data="home_feed" fallback={<></>}>
                <ActiveConversations conversations={filteredConversations} workerAvatars={workerAvatars} />
              </Deferred>
              <Deferred data="home_feed" fallback={<></>}>
                <RecentArtifacts artifacts={filteredArtifacts} />
              </Deferred>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

HomeIndex.layout = (page: React.ReactElement) => <DashboardLayout>{page}</DashboardLayout>

export default HomeIndex
